import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";
import api from "../../lib/api";
import { Field } from "./FormBits";
import { VENDOR_CATEGORIES, ADDONS, MB_FOOD_POLICY_URL, MB_FOOD_APPLICATION_URL } from "../../lib/data";
import { startCheckout } from "../../lib/payments";
export const VendorForm = () => {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "", category: "", description: "",
  });
  const [addons, setAddons] = useState([]);
  const [checks, setChecks] = useState({
    health: false, licensed: false, foodpermit: false, neutrality: false, liability: false, baked: false,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cat = VENDOR_CATEGORIES.find((c) => c.key === form.category);
  const boothFee = cat?.price || 0;
  const addonTotal = addons.length * 10;
  const total = boothFee + addonTotal;

  const toggleAddon = (key) =>
    setAddons((a) => (a.includes(key) ? a.filter((x) => x !== key) : [...a, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (!cat) return toast.error("Please choose a vendor category");
    if (cat.key === "food" && (!checks.health || !checks.licensed || !checks.foodpermit))
      return toast.error("Please complete all licensed food vendor requirements");
    if (cat.key === "baked" && !checks.baked) return toast.error("Please accept the home-based baked goods agreement");
    if (cat.key === "government" && !checks.neutrality) return toast.error("Please accept the neutrality agreement");
    if (!checks.liability) return toast.error("Please accept the liability waiver");
    setBusy(true);
    try {
      const r = await api.post("/forms/vendor", {
        ...form,
        booth_size: "10x10",
        category: cat.label,
        addons: addons.map((k) => ADDONS.find((a) => a.key === k).label),
        health_compliance: checks.health,
        licensed_vendor: checks.licensed,
        food_handler_permit: checks.foodpermit,
        neutrality_agreement: checks.neutrality,
        liability_waiver: checks.liability,
        baked_goods_agreement: checks.baked,
        booth_fee: boothFee,
        total,
      });
      await startCheckout({ kind: "vendor", ref_id: r.data.id });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6" data-testid="vendor-form">
      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Business / Organization Name" required>
          <input className="vintage-input" required value={form.business_name} onChange={set("business_name")} data-testid="v-business" />
        </Field>
        <Field label="Contact Name" required>
          <input className="vintage-input" required value={form.contact_name} onChange={set("contact_name")} data-testid="v-contact" />
        </Field>
        <Field label="Email" required>
          <input type="email" className="vintage-input" required value={form.email} onChange={set("email")} data-testid="v-email" />
        </Field>
        <Field label="Phone" required>
          <input className="vintage-input" required value={form.phone} onChange={set("phone")} data-testid="v-phone" />
        </Field>
      </div>

      <Field label="Vendor Category" required>
        <select className="vintage-input" required value={form.category} onChange={set("category")} data-testid="v-category">
          <option value="">Select a category…</option>
          {VENDOR_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label} — ${c.price}</option>)}
        </select>
      </Field>

      {/* Category-specific notices */}
      {cat?.key === "food" && (
        <div className="border-2 border-rust bg-cream-muted p-4 space-y-3" data-testid="v-health-notice">
          <div className="flex items-center gap-2 text-rust"><AlertTriangle size={18} /><span className="eyebrow">Licensed Food Vendor Requirements</span></div>
          <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-rust w-4 h-4" checked={checks.licensed} onChange={(e) => setChecks({ ...checks, licensed: e.target.checked })} data-testid="v-licensed-check" />
            I confirm that I am a licensed food vendor.
          </label>
          <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-rust w-4 h-4" checked={checks.foodpermit} onChange={(e) => setChecks({ ...checks, foodpermit: e.target.checked })} data-testid="v-foodpermit-check" />
            I have a valid food handling permit.
          </label>
          <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-rust w-4 h-4" checked={checks.health} onChange={(e) => setChecks({ ...checks, health: e.target.checked })} data-testid="v-health-check" />
            I agree to comply with all Manitoba Health food handling / licensing requirements, and acknowledge that a CPTCA representative will verify compliance and that my booth is subject to inspection.
          </label>
          <div className="border-t-2 border-dashed border-rust/40 pt-3 mt-1 space-y-2" data-testid="v-mbhealth-links">
            <p className="text-sm font-bold text-rust">Manitoba Health — Required Steps for Food Vendors</p>
            <ol className="list-decimal pl-5 text-sm text-forest-light space-y-1">
              <li>Review the <a href={MB_FOOD_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="v-mb-policy">Manitoba Health Food Safety Policy (PDF)</a>.</li>
              <li>Complete the <a href={MB_FOOD_APPLICATION_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="v-mb-application">Temporary Food Service Application (PDF)</a>.</li>
              <li>Email your completed application together with a copy of your food licence / permit to <a href="mailto:cptcamb@gmail.com" className="text-rust underline font-semibold">cptcamb@gmail.com</a>.</li>
            </ol>
          </div>
          <p className="text-xs text-forest-light/70">For more information, contact <span className="font-bold text-rust">204-951-8164</span>.</p>
        </div>
      )}

      {cat?.key === "baked" && (
        <div className="border-2 border-rust bg-cream-muted p-4" data-testid="v-baked-notice">
          <div className="flex items-center gap-2 mb-2 text-rust"><AlertTriangle size={18} /><span className="eyebrow">Home-Based Baked Goods — Manitoba Health</span></div>
          <p className="text-forest-light text-sm mb-3">Only non-cooked, home-based baked goods are permitted. Vendors must comply with all applicable Manitoba Health requirements for home-prepared / lower-risk baked goods, including safe handling, proper packaging and ingredient/allergen labeling. Participation is subject to review and approval by a CPTCA representative.</p>
          <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-rust w-4 h-4" checked={checks.baked} onChange={(e) => setChecks({ ...checks, baked: e.target.checked })} data-testid="v-baked-check" />
            I confirm my items are home-based baked goods (not cooked), I agree to comply with all applicable Manitoba Health requirements, and I understand my participation is subject to CPTCA representative approval.
          </label>
          <div className="border-t-2 border-dashed border-rust/40 pt-3 mt-3 space-y-2" data-testid="v-mbhealth-baked-links">
            <p className="text-sm font-bold text-rust">Manitoba Health — Required Steps</p>
            <ol className="list-decimal pl-5 text-sm text-forest-light space-y-1">
              <li>Review the <a href={MB_FOOD_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="v-baked-mb-policy">Manitoba Health Food Safety Policy (PDF)</a>.</li>
              <li>Complete the <a href={MB_FOOD_APPLICATION_URL} target="_blank" rel="noopener noreferrer" className="text-rust underline font-semibold" data-testid="v-baked-mb-application">Temporary Food Service Application (PDF)</a>.</li>
              <li>Email your completed application together with a copy of any licence / permit to <a href="mailto:cptcamb@gmail.com" className="text-rust underline font-semibold">cptcamb@gmail.com</a>.</li>
            </ol>
          </div>
        </div>
      )}

      {cat?.key === "government" && (
        <div className="border-2 border-forest bg-cream-muted p-4" data-testid="v-neutrality-notice">
          <div className="flex items-center gap-2 mb-2 text-forest"><Info size={18} /><span className="eyebrow">Neutrality Notice</span></div>
          <p className="text-forest-light text-sm mb-3">CPTCA welcomes officials and candidates from all parties on a first-come, equal-access basis. Booths are for community engagement only — no campaign solicitation, petitioning, or fundraising on-site.</p>
          <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-forest w-4 h-4" checked={checks.neutrality} onChange={(e) => setChecks({ ...checks, neutrality: e.target.checked })} data-testid="v-neutrality-check" />
            I have read and agree to the neutrality and equal-access terms above.
          </label>
        </div>
      )}

      <Field label="Add-ons (each +$10)">
        <div className="grid sm:grid-cols-2 gap-3 mt-1">
          {ADDONS.map((a) => (
            <label key={a.key} className={`flex items-center gap-2 border-2 px-3 py-2 cursor-pointer text-sm transition-colors ${addons.includes(a.key) ? "border-rust bg-rust/10" : "border-forest/40"}`} data-testid={`v-addon-${a.key}`}>
              <input type="checkbox" className="accent-rust w-4 h-4" checked={addons.includes(a.key)} onChange={() => toggleAddon(a.key)} />
              {a.label}
            </label>
          ))}
        </div>
      </Field>

      <Field label="What will you sell / showcase?">
        <textarea className="vintage-input min-h-[80px] resize-y" value={form.description} onChange={set("description")} data-testid="v-description" />
      </Field>

      {/* Live price summary */}
      <div className="ticket p-5" data-testid="v-price-summary">
        <p className="eyebrow text-rust mb-3">Live Price Summary</p>
        <div className="space-y-1 text-sm text-forest-light">
          <div className="flex justify-between"><span>Booth (10×10){cat ? ` — ${cat.label}` : ""}</span><span>${boothFee}</span></div>
          <div className="flex justify-between"><span>Add-ons ({addons.length} × $10)</span><span>${addonTotal}</span></div>
          <div className="dashed-divider my-2" />
          <div className="flex justify-between numeral text-2xl text-forest"><span>TOTAL</span><span data-testid="v-total">${total}</span></div>
        </div>
        <p className="text-[0.72rem] text-forest-light/70 mt-3">All booths are <strong>10×10</strong>. You must bring your own tent, and your own table &amp; chair unless rented above. Payment is required for approval to be processed — if your booth is not approved, your payment is refunded.</p>
      </div>

      <div className="border-2 border-dashed border-forest/50 p-3 text-sm text-forest-light" data-testid="v-paynote">
        Payment is by secure card (Stripe) at submission. <strong>Payment does not guarantee approval</strong> — your application is subject to CPTCA review, and if not approved you'll be fully refunded.
        <br />Payment issues or want an alternative like e-Transfer? Call <span className="font-bold text-rust">204-951-8164</span>.
      </div>

      <label className="flex items-start gap-3 text-sm text-forest-light cursor-pointer" data-testid="v-liability-wrap">
        <input type="checkbox" className="mt-0.5 accent-rust w-4 h-4" checked={checks.liability} onChange={(e) => setChecks({ ...checks, liability: e.target.checked })} data-testid="v-liability-check" />
        I accept the general liability waiver and understand that booth fees are non-refundable once my booth is approved and confirmed. *
      </label>

      <button type="submit" disabled={busy} className="btn-rust w-full py-4 text-base disabled:opacity-60" data-testid="v-submit">
        {busy ? "Submitting…" : `Apply & Pay $${total} by Card`}
      </button>
    </form>
  );
};
